import React, { useState } from "react";
import logo from "images/reskin/a-plus-logo.png";
import cn from "helpers/classnames";
import { ACCESS_TIERS } from "../../../constants";

const HeaderLogo = ({ isTeacher, isAPlus }) => {
  if (isTeacher || isAPlus) {
    const discountText = isAPlus ? "Up to 93% off" : "Up to 90% off";

    return (
      <>
        <img
          src={logo}
          alt="A+ logo"
          className="tw-h-7 tw-align-middle tw-mr-1 sm:tw-h-8"
        />
        {isTeacher && (
          <span className="tw-font-bold tw-text-teal body-1 tw-mr-1.5">
            Teacher
          </span>
        )}
        <div
          className={cn(
            "tw-rounded-[3px] tw-px-1.5 tw-py-0.5 tw-bg-bright-green tw-inline-flex tw-flex-col tw-items-start"
          )}
        >
          <div className="tw-justify-start tw-text-gray tw-text-xs tw-font-semibold">
            {discountText}
          </div>
        </div>
      </>
    );
  }
  return null;
};

const PlanCardBenefitsButton = ({ expanded, onToggle }) => {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "tw-w-full tw-h-full tw-inline-flex tw-flex-col tw-justify-center tw-items-center",
        "tw-bg-gray-tint-96 tw-border-b-[0] tw-border-x-[0] tw-rounded-b-[12px] tw-px-[10px] tw-py-1",
        "tw-border-t tw-border-t-gray-tint-80"
      )}
    >
      <div className="tw-flex">
        <div className="tw-inline-flex tw-flex-col tw-justify-start tw-items-start tw-p-1">
          <div className="tw-inline-flex tw-justify-center tw-items-center tw-rounded">
            <div className="tw-text-blue-shade-18 tw-text-base tw-font-semibold tw-font-['Nunito_Sans'] tw-leading-6">
              {expanded ? "View less" : "View features"}
            </div>
          </div>
        </div>
        <div className="tw-flex tw-items-center tw-relative tw-text-blue-shade-18">
          <i
            className={`lc-icon lc-icon-${expanded ? "up" : "down"} lc-icon-xs`}
          />
        </div>
      </div>
    </button>
  );
};

const GroupPlanCardBenefit = ({ benefit }) => {
  return (
    <li className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-[rgba(0,0,0,0.06)]">
      <div className="tw-flex tw-items-center">
        <i
          // Note: we may override the tw-text-green-shade-20 class via benefit.icon or benefit.mobile
          // but we add it here for Tailwind to pick it up when it purges
          className={`lc-icon tw-text-green-shade-20 ${
            benefit.mobile ?? benefit.icon
          } lc-icon-xs tw-mr-2 sm:tw-hidden`}
        />
        <i
          className={`lc-icon ${benefit.icon} lc-icon-xs tw-mr-2 tw-hidden sm:tw-inline`}
        />
        <span
          className="body-2 supported-text"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: benefit.text }}
        />
      </div>
    </li>
  );
};

const GroupPlanCard = ({ benefits = [], tier }) => {
  const [showBenefits, setShowBenefits] = useState(false);
  const isTeacher = tier === ACCESS_TIERS.A_PLUS_TEACHER;
  const isAPlus = tier === ACCESS_TIERS.A_PLUS;
  return (
    <div
      className={cn(
        "tw-w-full tw-rounded-xl tw-shadow-[0px_2px_10px_0px_rgba(24,25,25,0.25)]",
        "tw-max-w-[400px] sm:tw-w-[280px] tw-h-full tw-flex tw-flex-col sm:tw-flex-row"
      )}
    >
      <div
        className={cn(
          "tw-pb-3 tw-pt-4 tw-px-[15px] tw-bg-white tw-rounded-t-xl tw-w-full tw-items-center",
          "tw-inline-flex tw-flex-col tw-justify-start",
          "sm:tw-py-[15px] sm:tw-rounded-b-xl"
        )}
      >
        <div className="tw-self-stretch tw-pb-3 tw-flex tw-flex-col tw-justify-start tw-items-start tw-gap-2">
          <div className="tw-self-stretch tw-inline-flex tw-justify-start tw-items-center">
            <HeaderLogo isTeacher={isTeacher} isAPlus={isAPlus} />
          </div>

          <div className="tw-self-stretch tw-flex tw-flex-col tw-justify-start tw-items-center">
            <div className="tw-text-center body-3 tw-font-semibold">
              Each {isTeacher ? "teacher" : "student"} seat as low as
            </div>
            <div className="tw-text-center tw-justify-start">
              <span className="title-2 tw-line-through tw-text-gray-tint-50">
                ${isTeacher ? "95" : "71"}
              </span>
              <span className="title-2 tw-text-gray">
                {" "}
                ${isTeacher ? "10" : "5"}
              </span>
              <span className="body-2 tw-font-bold"> USD/year</span>
            </div>
          </div>
        </div>

        <div className="tw-self-start tw-hidden sm:tw-block benefits-list-container">
          <div className="tw-self-stretch tw-flex tw-flex-col tw-justify-start tw-items-start tw-gap-1">
            {benefits.map((benefit) => (
              <GroupPlanCardBenefit benefit={benefit} key={benefit.text} />
            ))}
          </div>
        </div>
        {/* On mobile views, we only want to show the supported benefits for the 'Free' plan. */}
        {showBenefits ? (
          <div className="sm:tw-hidden tw-flex tw-flex-col tw-self-start">
            {benefits.map((benefit) => (
              <GroupPlanCardBenefit benefit={benefit} key={benefit.text} />
            ))}
          </div>
        ) : null}
      </div>
      <div className="sm:tw-hidden">
        <PlanCardBenefitsButton
          expanded={showBenefits}
          onToggle={() => setShowBenefits(!showBenefits)}
        />
      </div>
    </div>
  );
};

export default GroupPlanCard;
